import { Node, Edge, NodeGroup, SequenceParticipant, SequenceMessage, SequenceFragment, SequenceParticipantGroup, Graph } from '../types';
import { NODE_TYPES, isMarkupType } from '../constants/nodeTypes';
import { generateId } from './index';
import { estimateSequenceMessageFootprint } from './sequenceMessageLayout';
import { clampFragmentAgainstNeighbors, recalcFragmentBounds } from './fragmentLayout';
import { SEQUENCE } from '../constants/sequence';
import type { RankAxis } from './elkLayout';
import { buildSocketsForDirection, calcNodeHeight, type NodeDirection } from './socketLayout';

/**
 * VELOSTRUCT <-> MERMAID EXCHANGE UTILITY
 * 
 * Supports:
 * - Exporting diagram state to Mermaid Flowchart (TD) syntax.
 * - Importing Mermaid Flowchart syntax with basic BFS-based auto-layout.
 * - Handles both legacy NodeGroup structure and new subgraph-based groups.
 */

// ── EXPORT ─────────────────────────────────────────────────────────────

/** Check if a node is a boundary node (group_input or group_output) */
const isBoundaryNode = (node: Node) => node.type === 'group_input' || node.type === 'group_output';
const isExportableNode = (node: Node) => !isBoundaryNode(node) && !isMarkupType(node.type);

export function diagramToMermaid(
    nodes: Node[],
    edges: Edge[],
    groups: NodeGroup[],
    graphs?: Record<string, Graph>
): string {
    let output = 'flowchart TD\n';

    const renderNode = (node: Node) => {
        const label = node.label || node.type;
        // Mermaid shapes: []=rect, ()=round, ([])=stadium, [[ ]]=subroutine, (( ))=circle, > ]=flag, { }=rhombus, {{ }}=hex, [/ /]=para, [\ \]=para-inv, [/ \]=trap, [\ /]=trap-inv, [( )]=cyl
        if (node.type === 'database' || node.type === 'cylinder') return `${node.id}[("${label}")]`;
        if (node.type === 'person' || node.type === 'oval') return `${node.id}(("${label}"))`;
        if (node.type === 'decision' || node.type === 'diamond') return `${node.id}{"${label}"}`;
        if (node.type === 'api') return `${node.id}[["${label}"]]`;
        if (node.type === 'external' || node.type === 'system' || node.type === 'rounded') return `${node.id}(["${label}"])`;
        if (node.type === 'hexagon') return `${node.id}{{"${label}"}}`;
        if (node.type === 'queue' || node.type === 'triangle') return `${node.id}>"${label}"]`;
        if (node.type === 'group') return `${node.id}[/"${label}"\\]`; // Trapezoid for groups
        return `${node.id}["${label}"]`;
    };

    // Build subgraph from a group node with childGraphId (new subgraph model)
    const buildSubgraphFromNode = (groupNode: Node, depth: number): string => {
        if (!groupNode.childGraphId || !graphs) return '';
        const childGraph = graphs[groupNode.childGraphId];
        if (!childGraph) return '';

        const indent = '  '.repeat(depth + 1);
        let str = `${indent}subgraph ${groupNode.id} ["${groupNode.label}"]\n`;

        // Add child nodes (excluding boundary nodes)
        childGraph.nodes
            .filter(n => isExportableNode(n))
            .forEach(node => {
                if (node.childGraphId && graphs) {
                    // Nested group - recurse
                    str += buildSubgraphFromNode(node, depth + 1);
                } else {
                    str += `${indent}  ${renderNode(node)}\n`;
                }
            });

        str += `${indent}end\n`;
        return str;
    };

    // Legacy: Recursive function to build subgraphs from NodeGroup
    const buildSubgraph = (groupId: string, depth: number) => {
        const group = groups.find(g => g.id === groupId);
        if (!group) return '';

        const indent = '  '.repeat(depth + 1);
        let str = `${indent}subgraph ${group.id} ["${group.label}"]\n`;

        // Add child nodes
        group.childNodeIds.forEach(nid => {
            const node = nodes.find(n => n.id === nid);
            if (node && isExportableNode(node)) str += `${indent}  ${renderNode(node)}\n`;
        });

        // Add nested groups
        group.childGroupIds.forEach(cgid => {
            str += buildSubgraph(cgid, depth + 1);
        });

        str += `${indent}end\n`;
        return str;
    };

    // Filter out boundary nodes from the main node list
    const realNodes = nodes.filter(n => isExportableNode(n));

    // 1. Process group nodes with childGraphId (new subgraph model)
    if (graphs) {
        realNodes
            .filter(n => n.childGraphId && !n.parentGroupId)
            .forEach(groupNode => {
                output += buildSubgraphFromNode(groupNode, 0);
            });
    }

    // 2. Process top-level legacy groups (groups that don't have a parent)
    groups.filter(g => !g.parentGroupId && !g.isFrame).forEach(group => {
        output += buildSubgraph(group.id, 0);
    });

    // 3. Process orphan nodes (nodes that don't have a parent group and aren't group nodes already processed)
    realNodes
        .filter(n => !n.parentGroupId && !n.childGraphId)
        .forEach(node => {
            output += `  ${renderNode(node)}\n`;
        });

    // 4. Process edges (excluding edges to/from boundary nodes)
    const boundaryNodeIds = new Set(nodes.filter(isBoundaryNode).map(n => n.id));
    edges
        .filter(e => !boundaryNodeIds.has(e.sourceNodeId) && !boundaryNodeIds.has(e.targetNodeId))
        .forEach(edge => {
            const label = edge.label ? `|"${edge.label}"| ` : '';
            output += `  ${edge.sourceNodeId} -->${label}${edge.targetNodeId}\n`;
        });

    return output;
}

export function diagramToMermaidSequence(
    participants: SequenceParticipant[],
    messages: SequenceMessage[],
    fragments: SequenceFragment[],
    participantGroups?: SequenceParticipantGroup[]
): string {
    let output = 'sequenceDiagram\n';

    const sorted = [...participants].sort((a, b) => a.order - b.order);
    const groupedIds = new Set((participantGroups ?? []).flatMap(g => g.childParticipantIds));

    // Emit participant groups using box syntax, and ungrouped participants
    (participantGroups ?? []).forEach(g => {
        output += `  box ${g.label}\n`;
        const children = sorted.filter(p => g.childParticipantIds.includes(p.id));
        children.forEach(p => {
            output += `    %% @velostruct:participant id=${p.id} type=${p.type} color=${p.color} x=${p.x}\n`;
            const keyword = p.type === 'person' ? 'actor' : 'participant';
            output += `    ${keyword} ${p.id} as ${p.label}\n`;
        });
        output += `  end\n`;
    });

    // Emit ungrouped participants
    sorted.filter(p => !groupedIds.has(p.id)).forEach(p => {
        output += `  %% @velostruct:participant id=${p.id} type=${p.type} color=${p.color} x=${p.x}\n`;
        const keyword = p.type === 'person' ? 'actor' : 'participant';
        output += `  ${keyword} ${p.id} as ${p.label}\n`;
    });

    // Map message type to Mermaid arrow syntax
    const arrowMap: Record<string, string> = {
        sync: '->>',
        async: '-->>',
        return: '--)',
    };

    const emitMessage = (msg: SequenceMessage) => {
        const metaParts: string[] = [];
        if (msg.description) metaParts.push(`description="${msg.description}"`);
        if (msg.payload) metaParts.push(`payload='${msg.payload}'`);
        if (msg.constraints) metaParts.push(`constraints="${msg.constraints}"`);
        if (metaParts.length > 0) {
            output += `  %% @velostruct:message id=${msg.id} ${metaParts.join(' ')}\n`;
        }
        const arrow = arrowMap[msg.type] ?? '->>';
        output += `  ${msg.sourceId}${arrow}${msg.targetId}: ${msg.label}\n`;
    };

    // Emit messages with nested fragment open/close matching parentFragmentId.
    const sortedMsgs = [...messages].sort((a, b) => a.y - b.y);
    const fragById = new Map(fragments.map(fragment => [fragment.id, fragment]));
    const emittedFragIds = new Set<string>();
    const openFragIds: string[] = [];
    const elseEmitted = new Set<string>();

    const ancestry = (fragId: string | undefined): string[] => {
        const chain: string[] = [];
        let current = fragId;
        const seen = new Set<string>();
        while (current && !seen.has(current)) {
            seen.add(current);
            chain.unshift(current);
            current = fragById.get(current)?.parentFragmentId;
        }
        return chain;
    };
    const emitFragmentOpen = (fragId: string) => {
        const frag = fragById.get(fragId);
        if (!frag) return;
        const fragMetaParts = [
            `id=${frag.id}`,
            `participantIds=${frag.participantIds.join(',')}`,
        ];
        if (frag.parentFragmentId) fragMetaParts.push(`parentFragmentId=${frag.parentFragmentId}`);
        if (frag.type === 'alt' && frag.altDividerY !== undefined) fragMetaParts.push(`altDividerY=${Math.round(frag.altDividerY)}`);
        output += `  %% @velostruct:fragment ${fragMetaParts.join(' ')}\n`;
        output += `  ${frag.type} ${frag.label}\n`;
        emittedFragIds.add(fragId);
    };
    const maybeEmitElse = (fragId: string, messageY: number) => {
        const frag = fragById.get(fragId);
        if (!frag || frag.type !== 'alt' || frag.altDividerY === undefined) return;
        if (elseEmitted.has(fragId)) return;
        if (messageY >= frag.altDividerY) {
            output += `  else\n`;
            elseEmitted.add(fragId);
        }
    };

    sortedMsgs.forEach(msg => {
        const wanted = ancestry(msg.fragmentId);
        let shared = 0;
        while (shared < openFragIds.length && shared < wanted.length && openFragIds[shared] === wanted[shared]) {
            shared += 1;
        }
        while (openFragIds.length > shared) {
            output += `  end\n`;
            openFragIds.pop();
        }
        // Cross `else` on still-open ancestors before opening nested fragments.
        openFragIds.forEach(fragId => maybeEmitElse(fragId, msg.y));
        while (openFragIds.length < wanted.length) {
            const nextId = wanted[openFragIds.length];
            emitFragmentOpen(nextId);
            openFragIds.push(nextId);
            maybeEmitElse(nextId, msg.y);
        }
        emitMessage(msg);
    });

    while (openFragIds.length > 0) {
        output += `  end\n`;
        openFragIds.pop();
    }

    // Emit empty fragments (no messages assigned)
    fragments.forEach(frag => {
        if (!emittedFragIds.has(frag.id)) {
            emitFragmentOpen(frag.id);
            output += `  end\n`;
        }
    });

    return output;
}

// ── IMPORT ─────────────────────────────────────────────────────────────

interface ImportedData {
    nodes: Node[];
    edges: Edge[];
    groups: NodeGroup[];
    participants: SequenceParticipant[];
    messages: SequenceMessage[];
    fragments: SequenceFragment[];
    participantGroups: SequenceParticipantGroup[];
    /**
     * Which way the source said the chart flows. Import only forms the graph;
     * the caller lays it out, and this is the one layout decision that is in
     * the Mermaid text rather than in the shape of the graph.
     */
    rankAxis?: RankAxis;
}

export type MermaidSubgraphMode = 'frames' | 'groups';
export type MermaidNodeStyle = 'nodes' | 'shapes';

export interface MermaidImportOptions {
    subgraphMode?: MermaidSubgraphMode;
    nodeStyle?: MermaidNodeStyle;
    /**
     * The text scale of the page being imported into. Boxes are fitted to the
     * label at the size that page will actually draw it, so a page set to a
     * larger text size does not import a diagram full of ellipses.
     */
    textScale?: number;
}

const FRAME_PALETTE = ['#6366f1', '#0ea5e9', '#10b981', '#f59e0b', '#ec4899', '#8b5cf6', '#14b8a6', '#f97316'];

export function mermaidToDiagram(mmd: string, options?: MermaidImportOptions): ImportedData {
    if (mmd.trim().startsWith('sequenceDiagram')) {
        return parseMermaidSequence(mmd);
    }

    const subgraphMode: MermaidSubgraphMode = options?.subgraphMode === 'groups' ? 'groups' : 'frames';
    const nodeStyle: MermaidNodeStyle = options?.nodeStyle === 'shapes' ? 'shapes' : 'nodes';
    const textScale = options?.textScale && options.textScale > 0 ? options.textScale : 1;
    const fallbackType = nodeStyle === 'shapes' ? 'box' : 'service';
    const nodes: Node[] = [];
    const edges: Edge[] = [];
    const groups: NodeGroup[] = [];

    const lines = mmd.split('\n');
    let rankAxis: RankAxis = 'ttb';
    const currentGroupStack: string[] = [];
    const groupIdMap = new Map<string, string>();

    lines.forEach(line => {
        const trimmed = line.trim();
        if (!trimmed || trimmed.startsWith('%%')) return;

        const dirMatch = trimmed.match(/^(?:flowchart|graph)\s+(TD|TB|BT|LR|RL)\b/i);
        if (dirMatch) {
            const dir = dirMatch[1].toUpperCase();
            rankAxis = dir === 'LR' || dir === 'RL' ? 'ltr' : 'ttb';
            return;
        }
        if (/^(flowchart|graph)\b/i.test(trimmed)) return;
        if (/^(classDef|class|linkStyle|style|click)\b/i.test(trimmed)) return;

        const subgraphMatch = trimmed.match(/^subgraph\s+([A-Za-z0-9_]+)(?:\s*\[\s*(.*?)\s*\]|\s+(.+))?$/i);
        if (subgraphMatch) {
            const mId = subgraphMatch[1];
            let label = subgraphMatch[2] ?? subgraphMatch[3] ?? mId;
            label = cleanLabel(label);
            const id = generateId('group');
            groupIdMap.set(mId, id);
            const parentId = currentGroupStack.length > 0 ? currentGroupStack[currentGroupStack.length - 1] : undefined;
            const newGroup: NodeGroup = {
                id,
                label,
                color: FRAME_PALETTE[groups.length % FRAME_PALETTE.length],
                childNodeIds: [],
                childGroupIds: [],
                parentGroupId: parentId,
                collapsed: false,
                position: { x: 0, y: 0 },
                size: { width: 200, height: 150 },
                isFrame: subgraphMode === 'frames',
            };
            if (parentId) {
                const parent = groups.find(g => g.id === parentId);
                if (parent) parent.childGroupIds.push(id);
            }
            groups.push(newGroup);
            currentGroupStack.push(id);
            return;
        }

        if (trimmed.toLowerCase() === 'end') {
            currentGroupStack.pop();
            return;
        }

        const parsedEdge = parseMermaidEdge(trimmed);
        if (parsedEdge) {
            edges.push({
                id: generateId('edge'),
                sourceNodeId: parsedEdge.src,
                sourceSocketId: 'out-0',
                targetNodeId: parsedEdge.tgt,
                targetSocketId: 'in-0',
                label: parsedEdge.label,
                dashStyle: parsedEdge.dotted ? 'dashed' : undefined,
            });
            ensureNode(parsedEdge.src, nodes, currentGroupStack, groups, undefined, undefined, fallbackType);
            ensureNode(parsedEdge.tgt, nodes, currentGroupStack, groups, undefined, undefined, fallbackType);
            return;
        }

        const nodeMatch = trimmed.match(/^([A-Za-z0-9_]+)([\[({>].*[\])}])?$/i);
        if (nodeMatch) {
            const id = nodeMatch[1];
            const shapePart = nodeMatch[2] || '';
            const nodeType = inferTypeFromShape(shapePart, nodeStyle);
            let label = shapePart ? cleanLabel(shapePart) : id;
            if (!label) label = id;
            ensureNode(id, nodes, currentGroupStack, groups, label, nodeType, fallbackType);
        }
    });

    // Import forms the graph and stops there. Sizes and one socket per edge are
    // part of the graph - a box has to be big enough for its label and an edge
    // has to know where it attaches - but where anything sits is a layout
    // question, and it is answered by the same run that answers it for Beautify.
    fanOutMermaidSockets(nodes, edges, rankAxis, textScale);

    return { nodes, edges, groups, participants: [], messages: [], fragments: [], participantGroups: [], rankAxis };
}

function parseMermaidEdge(trimmed: string): { src: string; tgt: string; label?: string; dotted?: boolean } | null {
    const dottedLabeled = trimmed.match(/^([A-Za-z0-9_]+)\s*-\.\s*(.*?)\s*\.->\s*([A-Za-z0-9_]+)/);
    if (dottedLabeled) {
        return { src: dottedLabeled[1], tgt: dottedLabeled[3], label: cleanEdgeLabel(dottedLabeled[2]), dotted: true };
    }
    const dotted = trimmed.match(/^([A-Za-z0-9_]+)\s*-\.->\s*([A-Za-z0-9_]+)/);
    if (dotted) return { src: dotted[1], tgt: dotted[2], dotted: true };

    const labeledPipe = trimmed.match(/^([A-Za-z0-9_]+)\s*-->\s*\|([^|]*)\|\s*([A-Za-z0-9_]+)/);
    if (labeledPipe) return { src: labeledPipe[1], tgt: labeledPipe[3], label: cleanEdgeLabel(labeledPipe[2]) };

    const labeledInline = trimmed.match(/^([A-Za-z0-9_]+)\s*--\|([^|]*)\|-->\s*([A-Za-z0-9_]+)/);
    if (labeledInline) return { src: labeledInline[1], tgt: labeledInline[3], label: cleanEdgeLabel(labeledInline[2]) };

    const thick = trimmed.match(/^([A-Za-z0-9_]+)\s*==>\s*([A-Za-z0-9_]+)/);
    if (thick) return { src: thick[1], tgt: thick[2] };

    const plain = trimmed.match(/^([A-Za-z0-9_]+)\s*-->\s*([A-Za-z0-9_]+)/);
    if (plain) return { src: plain[1], tgt: plain[2] };

    return null;
}

function cleanEdgeLabel(raw?: string): string | undefined {
    if (!raw) return undefined;
    let label = raw.replace(/^["']+|["']+$/g, '').trim();
    label = label.replace(/<br\s*\/?>/gi, '\n');
    return label || undefined;
}

/** Bodies NodeComponent draws without an icon badge, so the label gets more room. */
const SHAPED_MERMAID_TYPES = new Set(['diamond', 'database', 'queue', 'cylinder']);

/** Spacing between adjacent anchors on one edge of an imported node. */
const MERMAID_SOCKET_PITCH = 22;
/** Matches the inset socketX() reserves at each end of a node edge. */
const MERMAID_SOCKET_PAD = 28;

function mermaidNodeSize(
    label: string,
    type: string,
    axis: RankAxis,
    fanIn = 1,
    fanOut = 1,
    textScale = 1,
): { width: number; height: number } {
    // NodeComponent draws the label at 13px/600 inside `w - 42` (or `w - 16` on
    // shaped bodies, which have no icon badge or gear). Under-reserving here is
    // what turns "Grafana Dashboards" into "Grafana…" on canvas - so the per-
    // character estimate and both bounds move with the page's text scale, which
    // is the size the label will really be drawn at.
    const chrome = SHAPED_MERMAID_TYPES.has(type) ? 22 : 48;
    const labelW = Math.min(
        Math.round(260 * textScale),
        Math.max(Math.round(120 * textScale), Math.round(label.length * 7.6 * textScale) + chrome),
    );
    if (axis === 'ttb') {
        // Widen enough that every edge gets its own anchor along the top/bottom edge.
        const anchorW = 2 * MERMAID_SOCKET_PAD + (Math.max(fanIn, fanOut) - 1) * MERMAID_SOCKET_PITCH;
        return {
            width: Math.max(labelW, anchorW),
            height: type === 'database' || type === 'cylinder' ? 76 : 56,
        };
    }
    return {
        width: Math.max(labelW, 160),
        height: Math.max(calcNodeHeight(fanIn, fanOut), type === 'database' ? 88 : 72),
    };
}

/**
 * One socket per edge instead of a single clubbed In/Out.
 *
 * Clubbing matches Mermaid's look but destroys attribution: four topics failing
 * into one dead-letter node all land on the same pixel, so the reader cannot
 * tell four lines from one. Giving each edge its own anchor makes the fan
 * countable and traceable.
 */
function fanOutMermaidSockets(nodes: Node[], edges: Edge[], axis: RankAxis, textScale = 1): void {
    const direction: NodeDirection = axis === 'ttb' ? 'ttb' : 'ltr';
    const inCount = new Map<string, number>();
    const outCount = new Map<string, number>();
    for (const edge of edges) {
        outCount.set(edge.sourceNodeId, (outCount.get(edge.sourceNodeId) ?? 0) + 1);
        inCount.set(edge.targetNodeId, (inCount.get(edge.targetNodeId) ?? 0) + 1);
    }

    for (const node of nodes) {
        const fanIn = Math.max(1, inCount.get(node.id) ?? 0);
        const fanOut = Math.max(1, outCount.get(node.id) ?? 0);
        node.size = mermaidNodeSize(node.label, node.type, axis, fanIn, fanOut, textScale);
        node.sockets = buildSocketsForDirection(
            NODE_TYPES[node.type as keyof typeof NODE_TYPES] ? node.type : 'service',
            node.size.width,
            node.size.height,
            direction,
            // Every node type ships exactly one input and one output; top up to the fan count.
            Array.from({ length: fanIn - 1 }, () => ({ name: '' })),
            Array.from({ length: fanOut - 1 }, () => ({ name: '' })),
        ).map(socket => ({ ...socket, label: '' }));
        node.data = { ...node.data, direction };
    }

    // Provisional; assignSocketsByGeometry re-orders once positions exist.
    const nextIn = new Map<string, number>();
    const nextOut = new Map<string, number>();
    for (const edge of edges) {
        const outIndex = nextOut.get(edge.sourceNodeId) ?? 0;
        const inIndex = nextIn.get(edge.targetNodeId) ?? 0;
        edge.sourceSocketId = `out-${outIndex}`;
        edge.targetSocketId = `in-${inIndex}`;
        nextOut.set(edge.sourceNodeId, outIndex + 1);
        nextIn.set(edge.targetNodeId, inIndex + 1);
    }
}

/** Infer a VeloStruct node type from the Mermaid shape brackets. */
function inferTypeFromShape(raw: string | undefined, style: MermaidNodeStyle): string {
    const trimmed = (raw || '').trim();
    if (style === 'shapes') {
        if (/^\[\(.*\)\]$/.test(trimmed)) return 'cylinder';
        if (/^\(\(.*\)\)$/.test(trimmed)) return 'oval';
        if (/^\{\{.*\}\}$/.test(trimmed)) return 'hexagon';
        if (/^\{.*\}$/.test(trimmed)) return 'diamond';
        if (/^\(\[.*\]\)$/.test(trimmed) || /^\(.*\)$/.test(trimmed)) return 'rounded';
        if (/^\[\[.*\]\]$/.test(trimmed)) return 'rounded';
        if (/^>.*\]$/.test(trimmed)) return 'triangle';
        return 'box';
    }
    // [(label)] — cylinder → database
    if (/^\[\(.*\)\]$/.test(trimmed)) return 'database';
    // ((label)) — circle → person
    if (/^\(\(.*\)\)$/.test(trimmed)) return 'person';
    // {label} — rhombus → diamond
    if (/^\{.*\}$/.test(trimmed) && !/^\{\{.*\}\}$/.test(trimmed)) return 'diamond';
    // [[label]] — subroutine → api
    if (/^\[\[.*\]\]$/.test(trimmed)) return 'api';
    // ([label]) — stadium → external
    if (/^\(\[.*\]\)$/.test(trimmed)) return 'external';
    // >label] — flag → queue
    if (/^>.*\]$/.test(trimmed)) return 'queue';
    // {{label}} — hexagon → decision
    if (/^\{\{.*\}\}$/.test(trimmed)) return 'decision';
    return 'service';
}

function cleanLabel(label: string): string {
    if (!label) return label;
    let text = label.trim();
    for (let i = 0; i < 3; i++) {
        if ((text.startsWith('"') && text.endsWith('"')) || (text.startsWith("'") && text.endsWith("'"))) {
            text = text.slice(1, -1).trim();
            continue;
        }
        const stripped = text.replace(/^[[(>]+|[>)}]+$/g, '').trim();
        if (stripped === text) break;
        text = stripped;
    }
    return text;
}

function ensureNode(mId: string, nodes: Node[], stack: string[], groups: NodeGroup[], label?: string, nodeType?: string, fallbackType = 'service') {
    if (nodes.some(n => n.id === mId)) {
        if (label) {
            const n = nodes.find(n => n.id === mId);
            if (n) n.label = label;
        }
        if (nodeType && nodeType !== fallbackType) {
            const n = nodes.find(n => n.id === mId);
            if (n && (n.type === fallbackType || n.type === 'service' || n.type === 'box')) n.type = nodeType;
        }
        return;
    }

    const parentId = stack.length > 0 ? stack[stack.length - 1] : undefined;
    const newNode: Node = {
        id: mId,
        type: nodeType || fallbackType,
        label: label || mId,
        position: { x: 0, y: 0 },
        size: { width: 180, height: 100 },
        sockets: [
            { id: 'in-0', type: 'input', dataType: 'data', label: 'Input', position: { x: 0, y: 40 }, order: 0 },
            { id: 'out-0', type: 'output', dataType: 'data', label: 'Output', position: { x: 180, y: 40 }, order: 0 }
        ],
        parentGroupId: parentId
    };

    nodes.push(newNode);
    if (parentId) {
        const parent = groups.find(g => g.id === parentId);
        if (parent) parent.childNodeIds.push(mId);
    }
}

function parseMermaidSequence(mmd: string): ImportedData {
    const participants: SequenceParticipant[] = [];
    const messages: SequenceMessage[] = [];
    const fragments: SequenceFragment[] = [];
    const participantGroups: SequenceParticipantGroup[] = [];
    const importParticipantPalette = [
        '#6366f1', '#10b981', '#f59e0b', '#ec4899', '#06b6d4',
        '#ef4444', '#8b5cf6', '#f97316', '#84cc16', '#14b8a6',
        '#e11d48', '#0ea5e9', '#22c55e', '#a855f7', '#3b82f6',
        '#9333ea', '#65a30d', '#0891b2', '#d97706', '#db2777',
    ];

    const lines = mmd.split('\n');
    let msgY = 140; // Starting Y for messages
    const MSG_SPACING = 50;
    const FRAGMENT_PAD_TOP = SEQUENCE.FRAGMENT_PAD_TOP;
    const FRAGMENT_PAD_TOP_ALT = SEQUENCE.FRAGMENT_PAD_TOP_ALT;
    const FRAGMENT_PAD_BOTTOM = SEQUENCE.FRAGMENT_PAD_BOTTOM;
    const FRAGMENT_PAD_BOTTOM_ALT = SEQUENCE.FRAGMENT_PAD_BOTTOM_ALT;
    const FRAGMENT_MIN_HEIGHT = SEQUENCE.FRAGMENT_MIN_HEIGHT;
    const ALT_DIVIDER_TOP_GAP = SEQUENCE.ALT_DIVIDER_TOP_GAP;
    const ALT_DIVIDER_BOTTOM_GAP = SEQUENCE.ALT_DIVIDER_BOTTOM_GAP;
    const ALT_DIVIDER_MESSAGE_PADDING = SEQUENCE.ALT_DIVIDER_MESSAGE_PADDING;
    const SEQUENCE_LABEL_SAFE_MARGIN = SEQUENCE.LABEL_SAFE_MARGIN;
    const MSG_VERTICAL_BUFFER = SEQUENCE.MESSAGE_VERTICAL_BUFFER;

    // Metadata from @velostruct comments
    let pendingMeta: Record<string, string> = {};
    let pendingFragMeta: Record<string, string> = {};
    const fragmentStack: Array<{
        id: string;
        type: 'loop' | 'alt' | 'opt' | 'par';
        label: string;
        startY: number;
        meta: Record<string, string>;
        parentFragmentId?: string;
        minContentTopY?: number;
        maxContentBottomY?: number;
        participantIds: Set<string>;
        altDividerY?: number;
    }> = [];
    let currentBoxLabel: string | null = null;
    let currentBoxParticipantIds: string[] = [];
    let lastParsedMessage: SequenceMessage | null = null;

    const getNextImportedParticipantColor = (): string => {
        const used = new Set(participants.map((participant) => participant.color.toLowerCase()));
        const paletteColor = importParticipantPalette.find((color) => !used.has(color.toLowerCase()));
        if (paletteColor) return paletteColor;
        const hue = Math.round((participants.length * 137.508) % 360);
        return `hsl(${hue} 70% 56%)`;
    };

    const ensureParticipant = (id: string, label?: string, type?: string) => {
        if (participants.some(p => p.id === id)) {
            if (label) {
                const p = participants.find(p => p.id === id);
                if (p) p.label = label;
            }
            return;
        }
        const order = participants.length;
        const x = 150 + order * 200;
        participants.push({
            id,
            type: type ?? 'service',
            label: label ?? id,
            color: getNextImportedParticipantColor(),
            order,
            x,
        });
    };

    const estimateMessageFootprint = (
        label: string,
        sourceId: string,
        targetId: string,
    ): { above: number; below: number } => {
        return estimateSequenceMessageFootprint(
            participants,
            label,
            sourceId,
            targetId,
            { safeMargin: SEQUENCE_LABEL_SAFE_MARGIN },
        );
    };
    const estimateMessageGap = (
        upper: { label: string; sourceId: string; targetId: string },
        lower: { label: string; sourceId: string; targetId: string },
    ): number => {
        const upperFootprint = estimateMessageFootprint(upper.label, upper.sourceId, upper.targetId);
        const lowerFootprint = estimateMessageFootprint(lower.label, lower.sourceId, lower.targetId);
        return upperFootprint.below + lowerFootprint.above + MSG_VERTICAL_BUFFER;
    };

    lines.forEach(line => {
        const trimmed = line.trim();
        if (!trimmed || trimmed.startsWith('sequenceDiagram')) return;

        // VeloStruct metadata comment
        if (trimmed.startsWith('%% @velostruct:participant')) {
            const meta = parseVelostructComment(trimmed);
            if (meta.id) pendingMeta = meta;
            return;
        }
        if (trimmed.startsWith('%% @velostruct:message')) {
            const meta = parseVelostructComment(trimmed);
            if (meta.id) pendingMeta = meta;
            return;
        }
        if (trimmed.startsWith('%% @velostruct:fragment')) {
            const meta = parseVelostructComment(trimmed);
            if (meta.id) pendingFragMeta = meta;
            return;
        }
        if (trimmed.startsWith('%%')) return;

        // Box syntax for participant groups: box Label
        const boxMatch = trimmed.match(/^box\s+(.*)/i);
        if (boxMatch) {
            currentBoxLabel = boxMatch[1].trim();
            currentBoxParticipantIds = [];
            return;
        }

        // Fragment start: loop/alt/opt/par
        const fragMatch = trimmed.match(/^(loop|alt|opt|par)\b\s*(.*)/i);
        if (fragMatch) {
            const fragId = pendingFragMeta.id || generateId('fragment');
            const parentId = fragmentStack.length > 0 ? fragmentStack[fragmentStack.length - 1].id : undefined;
            fragmentStack.push({
                id: fragId,
                type: fragMatch[1].toLowerCase() as 'loop' | 'alt' | 'opt' | 'par',
                label: fragMatch[2] || '',
                startY: msgY,
                meta: { ...pendingFragMeta },
                parentFragmentId: pendingFragMeta.parentFragmentId || parentId,
                participantIds: new Set<string>(),
            });
            pendingFragMeta = {};
            return;
        }

        // Branch separators inside control fragments
        if (/^else\b/i.test(trimmed)) {
            const currentFrag = fragmentStack.length > 0 ? fragmentStack[fragmentStack.length - 1] : undefined;
            if (currentFrag?.type === 'alt') {
                const previousY = lastParsedMessage?.y ?? (msgY - MSG_SPACING);
                const candidate = previousY + Math.round(MSG_SPACING / 2);
                currentFrag.altDividerY = currentFrag.altDividerY ?? candidate;
            }
            return;
        }
        if (/^and\b/i.test(trimmed)) {
            return;
        }

        // End keyword: closes either a fragment or a box
        if (trimmed.toLowerCase() === 'end') {
            if (fragmentStack.length > 0) {
                const frag = fragmentStack.pop()!;
                const minTopY = frag.minContentTopY ?? frag.startY;
                const maxBottomY = frag.maxContentBottomY ?? frag.startY;
                const topPad = frag.type === 'alt' ? FRAGMENT_PAD_TOP_ALT : FRAGMENT_PAD_TOP;
                const bottomPad = frag.type === 'alt' ? FRAGMENT_PAD_BOTTOM_ALT : FRAGMENT_PAD_BOTTOM;
                const startYRaw = minTopY - topPad;
                let endYRaw = maxBottomY + bottomPad;
                if (endYRaw - startYRaw < FRAGMENT_MIN_HEIGHT) {
                    endYRaw = startYRaw + FRAGMENT_MIN_HEIGHT;
                }
                const participantIds = frag.meta.participantIds
                    ? frag.meta.participantIds.split(',').filter(Boolean)
                    : (frag.participantIds.size > 0 ? Array.from(frag.participantIds) : participants.map(p => p.id));
                const draftFragment: SequenceFragment = {
                    id: frag.id,
                    type: frag.type,
                    label: frag.label,
                    participantIds,
                    startY: startYRaw,
                    endY: endYRaw,
                    parentFragmentId: frag.parentFragmentId,
                };
                const clamped = clampFragmentAgainstNeighbors(
                    { participants, messages, fragments: [...fragments, draftFragment] },
                    draftFragment,
                    startYRaw,
                    endYRaw,
                );
                const startY = clamped.startY;
                const endY = Math.max(clamped.endY, startY + FRAGMENT_MIN_HEIGHT);
                const parsedMetaDivider = frag.meta.altDividerY ? parseFloat(frag.meta.altDividerY) : Number.NaN;
                const dividerBase = Number.isFinite(parsedMetaDivider)
                    ? parsedMetaDivider
                    : (frag.altDividerY ?? (startY + (endY - startY) / 2));
                const minDividerY = startY + ALT_DIVIDER_TOP_GAP;
                const maxDividerY = endY - ALT_DIVIDER_BOTTOM_GAP;
                const directMessages = messages.filter((message) => message.fragmentId === frag.id);
                const topBranch = directMessages.filter((message) => message.y < dividerBase);
                const bottomBranch = directMessages.filter((message) => message.y >= dividerBase);
                let strictMinY = minDividerY;
                let strictMaxY = maxDividerY;
                if (topBranch.length > 0) {
                    strictMinY = Math.max(
                        strictMinY,
                        Math.max(...topBranch.map((message) => {
                            const footprint = estimateMessageFootprint(message.label, message.sourceId, message.targetId);
                            return message.y + footprint.below + ALT_DIVIDER_MESSAGE_PADDING;
                        })),
                    );
                }
                if (bottomBranch.length > 0) {
                    strictMaxY = Math.min(
                        strictMaxY,
                        Math.min(...bottomBranch.map((message) => {
                            const footprint = estimateMessageFootprint(message.label, message.sourceId, message.targetId);
                            return message.y - footprint.above - ALT_DIVIDER_MESSAGE_PADDING;
                        })),
                    );
                }
                const normalizedDividerY = maxDividerY > minDividerY
                    ? Math.max(minDividerY, Math.min(maxDividerY, dividerBase))
                    : (startY + (endY - startY) / 2);
                const strictNormalizedDividerY = strictMaxY >= strictMinY
                    ? Math.max(strictMinY, Math.min(strictMaxY, normalizedDividerY))
                    : normalizedDividerY;
                fragments.push({
                    id: frag.id,
                    type: frag.type,
                    label: frag.label,
                    participantIds,
                    startY,
                    endY,
                    altDividerY: frag.type === 'alt' ? strictNormalizedDividerY : undefined,
                    parentFragmentId: frag.parentFragmentId,
                });
            } else if (currentBoxLabel !== null) {
                participantGroups.push({
                    id: generateId('pgroup'),
                    label: currentBoxLabel,
                    color: participants.find((p) => currentBoxParticipantIds.includes(p.id))?.color ?? '#6366f1',
                    childParticipantIds: [...currentBoxParticipantIds],
                    collapsed: false,
                });
                currentBoxLabel = null;
                currentBoxParticipantIds = [];
            }
            return;
        }

        // Participant/Actor: participant id as Label, or actor id as Label
        const participantMatch = trimmed.match(/^(?:participant|actor)\s+([^\s]+)(?:\s+as\s+(.*))?/i);
        if (participantMatch) {
            const isActor = trimmed.startsWith('actor');
            const mId = pendingMeta.id || participantMatch[1];
            const label = cleanLabel(participantMatch[2] || participantMatch[1]);
            const type = pendingMeta.type || (isActor ? 'person' : 'service');
            const color = pendingMeta.color || getNextImportedParticipantColor();
            const x = pendingMeta.x ? parseFloat(pendingMeta.x) : (150 + participants.length * 200);

            if (participants.some(p => p.id === mId)) {
                const p = participants.find(p => p.id === mId)!;
                p.label = label;
                p.type = type;
                p.color = color;
                p.x = x;
            } else {
                participants.push({
                    id: mId,
                    type,
                    label,
                    color,
                    order: participants.length,
                    x,
                });
            }
            // Track participant in current box group
            if (currentBoxLabel !== null) {
                currentBoxParticipantIds.push(mId);
            }
            pendingMeta = {};
            return;
        }

        // Message: src->>tgt: label, src-->>tgt: label, src--)tgt: label
        const msgMatch = trimmed.match(/^([^\s-]+)\s*(->>|-->>|--\)|-[->x]+)\s*([^\s:]+)(?::\s*(.*))?/i);
        if (msgMatch) {
            const srcId = msgMatch[1];
            const arrowType = msgMatch[2];
            const tgtId = msgMatch[3];
            const label = (msgMatch[4] || '').trim().replace(/<br\s*\/?>/gi, '\n');

            ensureParticipant(srcId);
            ensureParticipant(tgtId);

            let type: 'sync' | 'async' | 'return' = 'sync';
            if (arrowType === '-->>') type = 'async';
            else if (arrowType === '--)') type = 'return';

            const currentFragmentId = fragmentStack.length > 0 ? fragmentStack[fragmentStack.length - 1].id : undefined;
            const candidateMessage: SequenceMessage = {
                id: pendingMeta.id || generateId('message'),
                sourceId: srcId,
                targetId: tgtId,
                y: msgY,
                label,
                type,
                description: pendingMeta.description,
                payload: pendingMeta.payload,
                constraints: pendingMeta.constraints,
                fragmentId: currentFragmentId,
            };
            const resolvedY = lastParsedMessage
                ? Math.max(msgY, lastParsedMessage.y + estimateMessageGap(lastParsedMessage, candidateMessage))
                : msgY;
            candidateMessage.y = resolvedY;
            messages.push(candidateMessage);
            const footprint = estimateMessageFootprint(label, srcId, tgtId);
            fragmentStack.forEach((frag) => {
                const topY = resolvedY - footprint.above;
                const bottomY = resolvedY + footprint.below;
                frag.minContentTopY = frag.minContentTopY === undefined
                    ? topY
                    : Math.min(frag.minContentTopY, topY);
                frag.maxContentBottomY = frag.maxContentBottomY === undefined
                    ? bottomY
                    : Math.max(frag.maxContentBottomY, bottomY);
                frag.participantIds.add(srcId);
                frag.participantIds.add(tgtId);
            });
            pendingMeta = {};
            lastParsedMessage = candidateMessage;
            msgY = resolvedY + MSG_SPACING;
        }
    });

    // Expand participant spacing based on the longest label exchanged between adjacent lanes.
    const sortedParticipants = [...participants].sort((a, b) => a.order - b.order);
    const maxLabelCharsBetween = (leftId: string, rightId: string): number => {
        return messages.reduce((maxChars, message) => {
            const isBetween =
                (message.sourceId === leftId && message.targetId === rightId) ||
                (message.sourceId === rightId && message.targetId === leftId);
            if (!isBetween) return maxChars;
            return Math.max(maxChars, message.label.trim().length);
        }, 0);
    };
    for (let idx = 1; idx < sortedParticipants.length; idx += 1) {
        const prev = sortedParticipants[idx - 1];
        const current = sortedParticipants[idx];
        const longestLabel = maxLabelCharsBetween(prev.id, current.id);
        const dynamicGap = Math.max(200, Math.min(460, 190 + longestLabel * 3.5));
        current.x = Math.max(current.x, prev.x + dynamicGap);
    }

    // Keep top-level fragments sequential and non-overlapping after padding normalization.
    const shiftFragmentSubtree = (fragmentId: string, deltaY: number) => {
        if (deltaY === 0) return;
        const fragmentById = new Map(fragments.map((fragment) => [fragment.id, fragment]));
        const getDescendantIds = (rootId: string): Set<string> => {
            const ids = new Set<string>([rootId]);
            const queue = [rootId];
            while (queue.length > 0) {
                const parentId = queue.shift()!;
                fragments
                    .filter((fragment) => fragment.parentFragmentId === parentId)
                    .forEach((child) => {
                        if (ids.has(child.id)) return;
                        ids.add(child.id);
                        queue.push(child.id);
                    });
            }
            return ids;
        };

        const subtreeIds = getDescendantIds(fragmentId);
        subtreeIds.forEach((id) => {
            const fragment = fragmentById.get(id);
            if (!fragment) return;
            fragment.startY += deltaY;
            fragment.endY += deltaY;
            if (fragment.type === 'alt' && fragment.altDividerY !== undefined) {
                fragment.altDividerY += deltaY;
            }
        });

        messages.forEach((message) => {
            if (message.fragmentId && subtreeIds.has(message.fragmentId)) {
                message.y += deltaY;
            }
        });
    };

    const leafFragments = fragments.filter((fragment) => !fragments.some((child) => child.parentFragmentId === fragment.id));
    const visited = new Set<string>();
    (leafFragments.length > 0 ? leafFragments : fragments).forEach((fragment) => {
        recalcFragmentBounds({ participants, messages, fragments }, fragment.id, visited);
    });

    const topLevelFragments = fragments
        .filter((fragment) => !fragment.parentFragmentId)
        .sort((a, b) => a.startY - b.startY);
    for (let idx = 1; idx < topLevelFragments.length; idx += 1) {
        const previous = topLevelFragments[idx - 1];
        const current = topLevelFragments[idx];
        const minStart = previous.endY + 8;
        if (current.startY < minStart) {
            shiftFragmentSubtree(current.id, minStart - current.startY);
        }
    }

    return { nodes: [], edges: [], groups: [], participants, messages, fragments, participantGroups };
}

/** Parse a %% @velostruct:type key=value key="value" comment into a map */
function parseVelostructComment(line: string): Record<string, string> {
    const result: Record<string, string> = {};
    // Remove the prefix
    const content = line.replace(/^%%\s*@velostruct:\w+\s*/, '');
    // Match key=value or key="value" or key='value'
    const regex = /(\w+)=(?:"([^"]*)"|'([^']*)'|(\S+))/g;
    let match;
    while ((match = regex.exec(content)) !== null) {
        result[match[1]] = match[2] ?? match[3] ?? match[4];
    }
    return result;
}


